import axios from 'axios';
import { deleteCookie, getCookie, setCookie } from 'cookies-next';
import { addMonths } from 'date-fns';
import { options } from '../utils/cookie';
import { authSuccess, logout } from '../store/auth';
import { handleResetData } from '../store/company';
import {
    AUTH_TOKEN_COOKIE,
    REFRESH_TOKEN_COOKIE,
} from '../data/globalConstants';
import { requestScope, sharedRefresh } from './request-scope.mjs';

const isServer = typeof window === 'undefined';

const api = axios.create({
    baseURL: process.env.API_URL,
    timeout: isServer ? 10_000 : 0,
});

export default api;

let clientRefresh = null;
const refreshContext = () =>
    isServer ? requestScope()?.refresh : clientRefresh;

export const setAuthToken = (token) => {
    if (!isServer) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        return;
    }
    const scope = requestScope();
    if (scope) scope.token = token;
};

export const setRefreshTokenInterceptor = (dispatch, req, res) => {
    const refresh = { dispatch, req, res };
    if (!isServer) {
        clientRefresh = refresh;
        return;
    }
    const scope = requestScope();
    if (scope) scope.refresh = refresh;
};

api.interceptors.request.use((config) => {
    if (!isServer) return config;
    const token = requestScope()?.token;
    if (token) config.headers.set('Authorization', `Bearer ${token}`);
    else config.headers.delete('Authorization');
    return config;
});

api.interceptors.response.use(undefined, async (error) => {
    const refresh = refreshContext();
    if (!refresh || !error.response) return Promise.reject(error);
    const { dispatch, req, res } = refresh;
    const originalRequest = error.config;
    const refreshToken = getCookie(REFRESH_TOKEN_COOKIE, { req, res });
    if (
        refreshToken &&
        error.response.status === 401 &&
        !originalRequest._retry
    ) {
        originalRequest._retry = true;
        try {
            const { token } = await setNewToken(
                dispatch,
                req,
                res,
                refreshToken
            );
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return api(originalRequest);
        } catch {
            return Promise.reject(error);
        }
    }
    return Promise.reject(error);
});

// Every caller routes through here - the 401 interceptor above and the server
// render in utils/auth.js - so one guard covers them all.
export const setNewToken = (dispatch, req, res, refreshToken) =>
    sharedRefresh(() => refreshSession(dispatch, req, res, refreshToken));

const refreshSession = async (dispatch, req, res, refreshToken) => {
    try {
        const refreshTokenData = await getNewToken({
            refreshToken,
        });
        const { token: newToken, refreshToken: newRefreshToken } =
            refreshTokenData.data;

        setCookie(AUTH_TOKEN_COOKIE, newToken, { ...options, req, res });
        setCookie(REFRESH_TOKEN_COOKIE, newRefreshToken, {
            ...options,
            req,
            res,
            expires: addMonths(new Date(), 1),
        });

        await dispatch(authSuccess(newToken));
        setAuthToken(newToken);

        return { token: newToken, refreshToken: newRefreshToken };
    } catch (err) {
        deleteCookie(AUTH_TOKEN_COOKIE, { ...options, req, res });
        deleteCookie(REFRESH_TOKEN_COOKIE, { ...options, req, res });
        dispatch(logout());
        dispatch(handleResetData());
        return Promise.reject(err);
    }
};

export const login = async (data) => {
    return api.post('/auth/login', data);
};

export const getMe = async () => {
    return api.get('users/me');
};

export const getNewToken = async (data) => {
    return api.post('/auth/refresh', data);
};

export const deactivateUser = async (data) => {
    try {
        return await api.post('/auth/deactivate-user', data);
    } catch (err) {
        throw wrapRequestError(err);
    }
};

export const activateUser = async (data) => {
    try {
        return await api.post('/auth/activate-user', data);
    } catch (err) {
        throw wrapRequestError(err);
    }
};

export const setProfileDeactivated = async (profileId, deactivated) =>
    (
        await api.post(
            `/profiles/${profileId}/${
                deactivated ? 'deactivate' : 'reactivate'
            }`
        )
    ).data.data;

export const fetchDeactivatedProfiles = async () =>
    (await api.get('/profiles/admin/deactivated')).data.data;

export const promoteUser = async (data) => {
    try {
        return await api.post('/auth/promote-user', data);
    } catch (err) {
        throw wrapRequestError(err);
    }
};

export const registration = async (data) => {
    try {
        return api.post('/auth/register/user', data);
    } catch (err) {
        throw wrapRequestError(err);
    }
};

export const journalistRegistration = async (data) => {
    try {
        return api.post('/auth/register/journalist', data);
    } catch (err) {
        throw wrapRequestError(err);
    }
};

// Callers read `.message`, so the wrapped Error keeps the same text as before.
// The axios response is kept as well: a server-side render has to tell a real
// 404 from a timeout or a 429 before it decides what to cache (see utils/isr).
const wrapRequestError = (err) => {
    const wrapped = new Error(err);
    wrapped.response = err?.response;
    wrapped.status = err?.response?.status;
    return wrapped;
};

export const getReq = async (url) => {
    try {
        return await api.get(`${url}`);
    } catch (err) {
        throw wrapRequestError(err);
    }
};

export const paymentPostRequest = async (url, data) => {
    return await api.post(`${url}`, data);
};

export const postReq = async (url, data) => {
    try {
        return await api.post(`${url}`, data);
    } catch (err) {
        throw wrapRequestError(err);
    }
};

export const putReq = async (url, data) => {
    try {
        return await api.put(`${url}`, data);
    } catch (err) {
        throw wrapRequestError(err);
    }
};

export const patchReq = async (url, data) => {
    try {
        return await api.patch(`${url}`, data);
    } catch (err) {
        throw wrapRequestError(err);
    }
};

export const deleteReq = async (url) => {
    try {
        return await api.delete(`${url}`);
    } catch (err) {
        throw wrapRequestError(err);
    }
};

export const fileUpload = async (url, file, onProgress = null) => {
    let multipart;
    try {
        multipart = api.interceptors.request.use((config) => {
            config.headers['Content-Type'] = 'multipart/form-data';

            if (onProgress) {
                config.onUploadProgress = (progressEvent) => {
                    const totalLength = progressEvent.lengthComputable
                        ? progressEvent.total
                        : progressEvent.target.getResponseHeader(
                              'content-length'
                          ) ||
                          progressEvent.target.getResponseHeader(
                              'x-decompressed-content-length'
                          );
                    const prc = Math.round(
                        (progressEvent.loaded * 100) / totalLength
                    );
                    onProgress(prc);
                };
            }
            return config;
        });
        return await api.post(`${url}`, file);
    } catch (err) {
        throw wrapRequestError(err);
    } finally {
        // Left in place after a failed upload, every later request went out as multipart.
        api.interceptors.request.eject(multipart);
    }
};

export const queryRequest = async (url, query) => {
    try {
        return await api.get(url, { params: query });
    } catch (err) {
        throw wrapRequestError(err);
    }
};

export const getSubscriptionProducts = async () => {
    try {
        return await api.get('/stripe/products');
    } catch (err) {
        throw wrapRequestError(err);
    }
};
